import { useRef, useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { preprocessForEdit, stripEditAttributes } from '../../utils/htmlPreprocessor';
import { EDIT_MODE_SCRIPT } from '../../utils/editModeScript';
import { applyEditsToHtml, getEditsFromIframe, clearEditsFromIframe } from '../../utils/editApplicator';
import { resolveAssetTemplates } from '../../utils/assetTemplateResolver';

/**
 * Edit Mode Preview Component
 * 
 * PROCESSING STAGES (enforces strict separation of structural vs display concerns):
 * 
 * 1. REVERTABLE: Temporary structural changes that must be undone before save
 *    - Example: Section visibility adjustments, temporary wrappers
 *    - Currently: Section style resets (display/visibility)
 * 
 * 2. PREPROCESSED (STRUCTURAL): Changes that affect edit tracking but must be stripped
 *    - Example: data-edit-id attributes, edit mode scripts
 *    - Storage: structuralHtml state (preserves {{asset:uuid}} templates)
 *    - Used for: Applying edits (structural integrity maintained)
 * 
 * 3. DISCARDABLE (DISPLAY): Non-structural transforms for display only
 *    - Example: {{asset:uuid}} → resolved URLs, inline preview data
 *    - Storage: displayHtml state (has resolved URLs)
 *    - Used for: Iframe display only (forgotten on save)
 * 
 * APPLY FLOW:
 *   User edits → Apply to structuralHtml (templates preserved) →
 *   Strip PREPROCESSED (edit IDs removed) →
 *   Revert REVERTABLE (temp styles removed) →
 *   DISCARDABLE forgotten (URLs discarded) →
 *   Save clean code with {{asset:uuid}} intact ✅
 * 
 * KEY BENEFIT: Asset URLs never pollute saved code, CSP compliance maintained
 */

interface EditModePreviewProps {
  code: string;
  projectId: string | null;
  onApplyChanges: (updatedCode: string) => void;
  onHasChangesUpdate?: (hasChanges: boolean) => void;
  previewKey: number;
}

export interface EditModePreviewHandle {
  applyChanges: () => void;
  iframeRef: React.RefObject<HTMLIFrameElement>;
  hasUnappliedChanges: boolean;
}

const EditModePreview = forwardRef<EditModePreviewHandle, EditModePreviewProps>(
  ({ code, projectId, onApplyChanges, onHasChangesUpdate, previewKey }, ref) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    // STRUCTURAL HTML: Preprocessed with edit IDs, preserves {{asset:uuid}} templates
    const [structuralHtml, setStructuralHtml] = useState('');
    // DISPLAY HTML: Resolved URLs for display only (discardable)
    const [displayHtml, setDisplayHtml] = useState('');
    const [hasUnappliedChanges, setHasUnappliedChanges] = useState(false);
    const [selectedElementInfo, setSelectedElementInfo] = useState<any>(null);

    // Process HTML through the proper stages:
    // Code → REVERTABLE → PREPROCESSED (structural) → DISCARDABLE (display)
    useEffect(() => {
      if (!code) return;

      const processCode = async () => {
        try {
          console.log('🔄 [EFFECT] Code changed, processing through stages...', {
            codeLength: code.length,
            previewKey,
            timestamp: new Date().toISOString()
          });
          
          // STAGE 1: REVERTABLE changes (none currently - placeholder for future)
          let workingCode = code;
          
          // STAGE 2: PREPROCESSED - Add edit IDs (structural, must be preserved)
          const preprocessed = preprocessForEdit(workingCode);
          
          // Inject edit mode script at the end of body
          const scriptTag = `<script>${EDIT_MODE_SCRIPT}</script>`;
          const preprocessedWithScript = preprocessed.replace('</body>', `${scriptTag}</body>`);
          
          // Store structural HTML (still has {{asset:uuid}} templates) ✅
          setStructuralHtml(preprocessedWithScript);
          console.log('✅ [STAGE 2] PREPROCESSED: Edit IDs added, templates preserved');
          
          // STAGE 3: DISCARDABLE - Resolve assets for display only (non-structural)
          let displayCode = preprocessedWithScript;
          if (projectId) {
            try {
              displayCode = await resolveAssetTemplates(preprocessedWithScript, projectId);
              console.log('✅ [STAGE 3] DISCARDABLE: Asset templates resolved to URLs (display only)');
            } catch (error) {
              console.error('⚠️ Failed to resolve asset templates (non-fatal):', error);
              // Continue with unresolved code for display
            }
          }
          
          // Store display HTML (has resolved URLs, will be discarded on apply) ✅
          setDisplayHtml(displayCode);
          console.log('✅ [EFFECT] HTML processed - iframe will display resolved version');
        } catch (error) {
          console.error('❌ Failed to process HTML:', error);
          // Fallback: use original code for both
          setStructuralHtml(code);
          setDisplayHtml(code);
        }
      };

      processCode();
    }, [code, previewKey, projectId]);

    // Listen for messages from iframe
    useEffect(() => {
      const handleMessage = (event: MessageEvent) => {
        // Only accept messages from our iframe
        if (event.source !== iframeRef.current?.contentWindow) return;

        const { type, editId, hasChanges, tagName, className, textContent } = event.data;

        switch (type) {
          case 'edit-mode-ready':
            console.log('✅ Edit mode ready in iframe');
            break;
          
          case 'selection-changed':
            if (editId) {
              setSelectedElementInfo({ editId, tagName, className, textContent });
              console.log('Selected element:', editId);
            } else {
              setSelectedElementInfo(null);
            }
            break;
          
          case 'edits-changed':
            setHasUnappliedChanges(hasChanges);
            onHasChangesUpdate?.(hasChanges);
            console.log('Edits changed, hasChanges:', hasChanges);
            break;
        }
      };

      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
    }, []);

    // Click outside to clear selection - DISABLED for easier property editing
    // useEffect(() => {
    //   const handleClickOutside = (event: MouseEvent) => {
    //     const iframe = iframeRef.current;
    //     if (!iframe) return;

    //     // Check if click was outside the iframe
    //     const iframeRect = iframe.getBoundingClientRect();
    //     const clickedOutside = 
    //       event.clientX < iframeRect.left ||
    //       event.clientX > iframeRect.right ||
    //       event.clientY < iframeRect.top ||
    //       event.clientY > iframeRect.bottom;

    //     if (clickedOutside && iframe.contentWindow) {
    //       // Send message to iframe to clear selection
    //       iframe.contentWindow.postMessage({ type: 'clear-selection' }, '*');
    //     }
    //   };

    //   document.addEventListener('click', handleClickOutside);
    //   return () => document.removeEventListener('click', handleClickOutside);
    // }, []);

    // Apply changes function - Uses STRUCTURAL HTML (with templates preserved)
    const applyChanges = () => {
      if (!iframeRef.current?.contentWindow) {
        console.error('Iframe not ready');
        return;
      }

      try {
        console.log('🚀 [APPLY-CHANGES] Starting apply changes flow...');
        
        const iframeDoc = iframeRef.current.contentWindow.document;
        
        // REVERTABLE: Reset section display/visibility styles before getting edits
        // This prevents temporary section activation styles from being saved
        const allSections = iframeDoc.querySelectorAll('section[section-name]');
        allSections.forEach(section => {
          const htmlSection = section as HTMLElement;
          // Remove temporary inline display/visibility styles
          htmlSection.style.removeProperty('display');
          htmlSection.style.removeProperty('visibility');
        });
        console.log('🔄 [APPLY-CHANGES] REVERTABLE: Reset section visibility styles');
        
        // Get edits from iframe localStorage
        const edits = getEditsFromIframe(iframeRef.current.contentWindow);
        console.log('📝 [APPLY-CHANGES] Retrieved edits from localStorage:', {
          count: Object.keys(edits).length,
          editIds: Object.keys(edits),
          timestamp: new Date().toISOString()
        });

        if (Object.keys(edits).length === 0) {
          console.log('⚠️ [APPLY-CHANGES] No edits to apply - localStorage might be empty');
          return;
        }

        console.log('📄 [APPLY-CHANGES] Using STRUCTURAL HTML (with {{asset:uuid}} templates preserved)');
        
        // Apply edits to STRUCTURAL HTML (which still has {{asset:uuid}} templates) ✅
        const htmlWithEdits = applyEditsToHtml(structuralHtml, edits);

        // PREPROCESSED: Strip edit attributes to get clean HTML (still has templates) ✅
        const cleanHtml = stripEditAttributes(htmlWithEdits);
        
        // DISCARDABLE: Display HTML with resolved URLs is forgotten (not saved) ✅

        console.log('🧹 [APPLY-CHANGES] Clearing localStorage...');
        // Clear edits from iframe
        clearEditsFromIframe(iframeRef.current.contentWindow);

        console.log('📤 [APPLY-CHANGES] Sending clean HTML to parent (templates preserved)');
        // Update parent with clean HTML (still has {{asset:uuid}} templates)
        onApplyChanges(cleanHtml);
        setHasUnappliedChanges(false);
        onHasChangesUpdate?.(false);

        console.log('✅ [APPLY-CHANGES] Apply changes flow complete - templates preserved ✅');
      } catch (error) {
        console.error('❌ [APPLY-CHANGES] Failed to apply changes:', error);
      }
    };

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      applyChanges,
      iframeRef,
      hasUnappliedChanges,
    }));

    return (
      <div className="w-full h-full relative overflow-hidden">
        <iframe
          ref={iframeRef}
          key={previewKey}
          srcDoc={displayHtml}
          sandbox="allow-scripts allow-same-origin allow-modals"
          className="border-0"
          title="Edit Mode Preview"
          style={{
            width: '400px',
            height: '80%',
            transform: 'scale(1.25)',
            transformOrigin: '0% 0%'
          }}
        />
      </div>
    );
  }
);

EditModePreview.displayName = 'EditModePreview';

export default EditModePreview;

